/**
 * PDF Parser via API for TranslyxCAT
 * Uses server-side PDF parsing API to extract text from PDF documents
 */

export interface PDFParseResult {
  segments: Array<{
    source: string;
    target: string;
    page_number?: number;
    bbox?: { x: number; y: number; width: number; height: number };
    font_meta?: { size: number; name: string };
  }>;
  wordCount: number;
}

/**
 * Parse PDF file via server API
 * @param file - PDF file to parse
 * @returns Promise with parsed segments and word count
 * @throws Error if API call fails or file is invalid
 */
export async function parsePDFViaAPI(file: File): Promise<PDFParseResult> {
  // Validate file
  if (!file) {
    throw new Error('No file provided');
  }

  // Check file type
  const fileExt = file.name.toLowerCase().split('.').pop();
  if (fileExt !== 'pdf' && !file.type.includes('pdf')) {
    throw new Error('Invalid file type. Please upload a PDF file.');
  }

  // Check file size (10MB limit)
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    throw new Error(`File too large. Maximum size is ${formatFileSize(maxSize)}.`);
  }

  try {
    // Create FormData
    const formData = new FormData();
    formData.append('file', file);

    // Make API request
    const response = await fetch(`${(import.meta as any).env?.VITE_API_URL || 'https://api.linguaproz.com'}/api/parse/pdf`, {
      method: 'POST',
      body: formData,
      // Note: Don't set Content-Type header for FormData - browser will set it automatically
    });

    // Handle response
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || `API request failed with status ${response.status}`;
      throw new Error(errorMessage);
    }

    // Parse successful response
    const result = await response.json();

    // Validate response structure
    if (!result.segments || !Array.isArray(result.segments)) {
      throw new Error('Invalid response from PDF parsing API');
    }

    // Ensure all segments have required fields
    const validatedSegments = result.segments.map((segment: any) => ({
      source: segment.source || '',
      target: segment.target || '',
      ...(segment.page_number !== undefined && { page_number: segment.page_number }),
      ...(segment.bbox !== undefined && { bbox: segment.bbox }),
      ...(segment.font_meta !== undefined && { font_meta: segment.font_meta }),
    }));

    return {
      segments: validatedSegments,
      wordCount: result.wordCount || 0,
    };

  } catch (error) {
    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }

    // Re-throw existing errors
    if (error instanceof Error) {
      throw error;
    }

    // Handle unknown errors
    throw new Error('Failed to parse PDF file. Please try again or use a different file.');
  }
}

/**
 * Format file size in human-readable format
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Check if PDF parsing API is available
 */
export async function isPDFParsingAPIAvailable(): Promise<boolean> {
  try {
    const response = await fetch(`${(import.meta as any).env?.VITE_API_URL || 'https://api.linguaproz.com'}/health`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    return response.ok;
  } catch {
    return false;
  }
}

export default parsePDFViaAPI;
